import { useState, useCallback } from 'react';
import { normalizeFarmers, normalizeProcessors } from '../utils/dataTransform';

// Custom hook for managing API requests with loading, error, and success states
export const useApi = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const execute = useCallback(async (apiCall) => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiCall();
      setData(result);
      return result;
    } catch (err) {
      setError(err.message || 'An error occurred');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setLoading(false);
    setError(null);
    setData(null);
  }, []);

  return {
    loading,
    error,
    data,
    execute,
    reset,
  };
};

// Hook specifically for processors data
export const useProcessors = () => {
  const [processors, setProcessors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchProcessors = useCallback(async (apiCall) => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiCall();
      const data = result.data || result;
      // Normalize data from snake_case (database) to camelCase (frontend)
      const normalized = normalizeProcessors(Array.isArray(data) ? data : []);
      setProcessors(normalized);
      return result;
    } catch (err) {
      setError(err.message || 'Failed to fetch processors');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const addProcessor = useCallback((newProcessor) => {
    setProcessors(prev => [...prev, newProcessor]);
  }, []);

  const updateProcessor = useCallback((id, updatedProcessor) => {
    setProcessors(prev => 
      prev.map(processor => 
        processor.id === id ? { ...processor, ...updatedProcessor } : processor
      )
    );
  }, []);

  const removeProcessor = useCallback((id) => {
    setProcessors(prev => prev.filter(processor => processor.id !== id));
  }, []);

  const reset = useCallback(() => {
    setProcessors([]);
    setLoading(false);
    setError(null);
  }, []);

  return {
    processors,
    loading,
    error,
    fetchProcessors,
    addProcessor,
    updateProcessor,
    removeProcessor,
    reset,
  };
};

// Hook for farmers data
export const useFarmers = () => {
  const [farmers, setFarmers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchFarmers = useCallback(async (apiCall) => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiCall();
      const data = result.data || result;
      // Normalize data from snake_case (database) to camelCase (frontend)
      const normalized = normalizeFarmers(Array.isArray(data) ? data : []);
      
      // Deduplicate farmers by ID (in case backend returns duplicate rows from JOINs)
      const uniqueFarmers = normalized.reduce((acc, farmer) => {
        if (farmer.id && !acc.find(f => f.id === farmer.id)) {
          acc.push(farmer);
        }
        return acc;
      }, []);
      setFarmers(uniqueFarmers);
      return result;
    } catch (err) {
      setError(err.message || 'Failed to fetch farmers');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const addFarmer = useCallback((newFarmer) => {
    setFarmers(prev => [...prev, newFarmer]);
  }, []);

  const updateFarmer = useCallback((id, updatedFarmer) => {
    setFarmers(prev => 
      prev.map(farmer => 
        farmer.id === id ? { ...farmer, ...updatedFarmer } : farmer
      )
    );
  }, []);

  const removeFarmer = useCallback((id) => {
    setFarmers(prev => prev.filter(farmer => farmer.id !== id));
  }, []);

  const reset = useCallback(() => {
    setFarmers([]);
    setLoading(false);
    setError(null);
  }, []);

  return {
    farmers,
    loading,
    error,
    fetchFarmers,
    addFarmer,
    updateFarmer,
    removeFarmer,
    reset,
  };
};